import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, CheckCircle, Briefcase } from "lucide-react";

interface ResumeMatchBreakdownProps {
  isOpen: boolean;
  onClose: () => void;
  resumeText: string;
  resumeType: 'original' | 'tailored';
  matchScore: number;
  jobRequirements: string[];
  jobResponsibilities: string[];
  jobSkills: string[];
  jobTitle?: string;
  companyName?: string;
}

export function ResumeMatchBreakdown({
  isOpen,
  onClose,
  resumeText,
  resumeType,
  matchScore,
  jobRequirements,
  jobResponsibilities,
  jobSkills,
  jobTitle = "Target Position",
  companyName = "Target Company"
}: ResumeMatchBreakdownProps) {
  
  // Ensure props are arrays to prevent .map() errors
  const safeJobRequirements = Array.isArray(jobRequirements) ? jobRequirements : [];
  const safeJobResponsibilities = Array.isArray(jobResponsibilities) ? jobResponsibilities : [];
  const safeJobSkills = Array.isArray(jobSkills) ? jobSkills : [];
  
  // Calculate detailed match breakdown
  const calculateDetailedMatches = () => {
    const normalizedText = resumeText.toLowerCase();
    
    // Requirements matches
    const requirementMatches = safeJobRequirements.map(req => {
      const normalized = req.toLowerCase();
      const keywords = normalized.split(/[\s,\.\-\(\)]+/).filter(word => word.length > 2);
      const hasMatch = keywords.some(keyword => normalizedText.includes(keyword));
      return { text: req, matched: hasMatch };
    });

    // Responsibilities matches
    const responsibilityMatches = safeJobResponsibilities.map(resp => {
      const normalized = resp.toLowerCase();
      const keywords = normalized.split(/[\s,\.\-\(\)]+/).filter(word => word.length > 2);
      const hasMatch = keywords.some(keyword => normalizedText.includes(keyword));
      return { text: resp, matched: hasMatch };
    });

    // Skills matches
    const skillMatches = safeJobSkills.map(skill => {
      const normalized = skill.toLowerCase();
      const hasMatch = normalizedText.includes(normalized) || 
                      normalized.split(/[\s,\.\-\(\)]+/).some(word => word.length > 2 && normalizedText.includes(word));
      return { text: skill, matched: hasMatch };
    });

    return { requirementMatches, responsibilityMatches, skillMatches };
  };

  const { requirementMatches, responsibilityMatches, skillMatches } = calculateDetailedMatches();
  
  // Calculate individual scores
  const requirementScore = safeJobRequirements.length > 0 ? 
    Math.round((requirementMatches.filter(r => r.matched).length / safeJobRequirements.length) * 100) : 0;
  const responsibilityScore = safeJobResponsibilities.length > 0 ? 
    Math.round((responsibilityMatches.filter(r => r.matched).length / safeJobResponsibilities.length) * 100) : 0;
  const skillScore = safeJobSkills.length > 0 ? 
    Math.round((skillMatches.filter(s => s.matched).length / safeJobSkills.length) * 100) : 0;

  const getScoreColor = (score: number) => {
    if (score >= 86) return "text-green-600";
    if (score >= 70) return "text-yellow-600"; 
    if (score >= 31) return "text-orange-600";
    return "text-red-600";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 86) return "Excellent Match";
    if (score >= 70) return "Good Match";
    if (score >= 31) return "Fair Match";  
    return "Poor Match";
  };

  const getQualificationStatus = (score: number) => {
    return score >= 70 ? "QUALIFIED to Apply" : "NOT QUALIFIED to Apply at this Time";
  };

  const getMissingItems = () => {
    return {
      requirements: requirementMatches.filter(r => !r.matched).slice(0, 3),
      responsibilities: responsibilityMatches.filter(r => !r.matched).slice(0, 3),
      skills: skillMatches.filter(s => !s.matched).slice(0, 3)
    };
  };

  const missing = getMissingItems();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            {resumeType === 'original' ? 'Original' : 'Tailored'} Resume Match Analysis
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overall Match Score */}
          <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
            <CardContent className="p-6">
              <div className="text-center">
                <h2 className="text-xl font-bold mb-4 flex items-center justify-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  {resumeType === 'original' ? 'Original Resume' : 'Tailored Resume'} Match Score
                </h2>
                <div className="flex items-center justify-center gap-6">
                  <div className={`text-4xl font-bold ${getScoreColor(matchScore)}`}>
                    {matchScore}%
                  </div>
                  <div className="text-left">
                    <p className={`text-lg font-semibold ${getScoreColor(matchScore)}`}>
                      {getScoreLabel(matchScore)}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      vs. {jobTitle} at {companyName}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Qualification Status */}
          <Card className={`border ${matchScore >= 70 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
            <CardContent className="p-4 text-center">
              <h3 className={`text-lg font-bold ${matchScore >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                {getQualificationStatus(matchScore)}
              </h3>
            </CardContent>
          </Card>

          {/* Individual Scores Breakdown */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                📈 Detailed Score Breakdown
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">🛠 Skills (20%)</span>
                    <span className="text-lg font-bold text-primary">{skillScore}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {skillMatches.filter(s => s.matched).length} of {safeJobSkills.length} skills found
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">💡 Responsibilities (30%)</span>
                    <span className="text-lg font-bold text-accent">{responsibilityScore}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {responsibilityMatches.filter(r => r.matched).length} of {safeJobResponsibilities.length} responsibilities found
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-success/10 border border-success/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">✔ Requirements (50%)</span>
                    <span className="text-lg font-bold text-success">{requirementScore}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {requirementMatches.filter(r => r.matched).length} of {safeJobRequirements.length} requirements found
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Found Matches */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-green-600">
                <CheckCircle className="w-6 h-6" />
                ✅ Found in {resumeType === 'original' ? 'Original' : 'Tailored'} Resume
              </h3>
              
              <div className="space-y-4">
                {skillMatches.filter(s => s.matched).length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 text-green-700 text-base flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      🛠 Technical Skills & Tools:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {skillMatches.filter(s => s.matched).map((skill, index) => (
                        <Badge key={index} variant="secondary" className="bg-green-100 text-green-800 px-3 py-2 text-sm font-medium">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {skill.text}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {responsibilityMatches.filter(r => r.matched).length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 text-green-700 text-base flex items-center gap-2">
                      <Briefcase className="w-5 h-5" />
                      💡 Key Responsibilities:
                    </h4>
                    <div className="space-y-2">
                      {responsibilityMatches.filter(r => r.matched).slice(0, 5).map((resp, index) => (
                        <div key={index} className="flex items-start gap-3 text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-green-800 font-medium leading-relaxed">{resp.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {requirementMatches.filter(r => r.matched).length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 text-green-700 text-base flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      ✔ Core Requirements:
                    </h4>
                    <div className="space-y-2">
                      {requirementMatches.filter(r => r.matched).slice(0, 5).map((req, index) => (
                        <div key={index} className="flex items-start gap-3 text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-green-800 font-medium leading-relaxed">{req.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Missing Areas */}
          {(missing.requirements.length > 0 || missing.responsibilities.length > 0 || missing.skills.length > 0) && (
            <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-orange-600">
                  ⚠ Areas for Improvement
                </h3>
                
                <div className="space-y-4">
                  {missing.requirements.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 text-orange-700">📋 Missing Requirements:</h4>
                      <div className="space-y-2">
                        {missing.requirements.map((item, index) => (
                          <div key={index} className="p-3 bg-white rounded-lg border border-orange-200">
                            <p className="text-sm font-medium text-orange-800">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {missing.responsibilities.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 text-orange-700">💼 Missing Responsibilities:</h4>
                      <div className="space-y-2">
                        {missing.responsibilities.map((item, index) => (
                          <div key={index} className="p-3 bg-white rounded-lg border border-orange-200">
                            <p className="text-sm font-medium text-orange-800">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {missing.skills.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 text-orange-700">🧠 Missing Skills:</h4>
                      <div className="space-y-2">
                        {missing.skills.map((item, index) => (
                          <div key={index} className="p-3 bg-white rounded-lg border border-orange-200">
                            <p className="text-sm font-medium text-orange-800">{item.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Steps and Areas for Continued Growth */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-blue-600">
                🚀 Next Steps & Areas for Continued Growth
              </h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 text-blue-700">📈 Immediate Actions:</h4>
                  <div className="space-y-2">
                    {matchScore < 70 && (
                      <div className="p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800">
                          • Add missing keywords and skills identified in the analysis above
                        </p>
                      </div>
                    )}
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • Quantify achievements with specific metrics and numbers where possible
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • Align job descriptions with the target role's responsibilities
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 text-blue-700">🎯 Long-term Development:</h4>
                  <div className="space-y-2">
                    {missing.skills.length > 0 && (
                      <div className="p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800">
                          • Develop expertise in: {missing.skills.slice(0, 3).map(s => s.text).join(', ')}
                        </p>
                      </div>
                    )}
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • Gain experience in industry-specific tools and technologies
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • Consider relevant certifications or training programs
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 text-blue-700">💼 Application Strategy:</h4>
                  <div className="space-y-2">
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • {matchScore >= 70 ? 'Strong candidate - apply confidently' : 'Consider gaining more experience before applying'}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • Network with professionals in similar roles at the target company
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        • Prepare interview responses highlighting your matching qualifications
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}